import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import googleImg from "../assets/images/google.png";
import microsoftImg from "../assets/images/microsoft.svg";
import facebookImg from "../assets/images/facebook-svgrepo-com.svg";
import { useNavigate } from "react-router-dom";
import useSocialLogin from "../hooks/useSocialLogin";
import { toast } from "react-toastify";
import { applyTranslationDynamically } from "../common/TranslationHelper";
import {
  GoogleOAuthProvider,
  useGoogleLogin,
  GoogleLogin,
} from "@react-oauth/google";

// Social Login Configuration: Set to false to disable individual buttons,
// or configure via environment variables (defaults to true if undefined)
const ENABLE_GOOGLE =
  process.env.REACT_APP_ENABLE_GOOGLE_LOGIN === undefined
    ? true
    : process.env.REACT_APP_ENABLE_GOOGLE_LOGIN === "true";
const ENABLE_MICROSOFT =
  process.env.REACT_APP_ENABLE_MICROSOFT_LOGIN === undefined
    ? true
    : process.env.REACT_APP_ENABLE_MICROSOFT_LOGIN === "true";
const ENABLE_FACEBOOK =
  process.env.REACT_APP_ENABLE_FACEBOOK_LOGIN === undefined
    ? true
    : process.env.REACT_APP_ENABLE_FACEBOOK_LOGIN === "true";

const GoogleLoginButton = ({ onSuccess, onError, isLoading }) => {
  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Exchange the authorization code for tokens
        const response = await fetch("https://oauth2.googleapis.com/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            code: tokenResponse.code,
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
            redirect_uri: process.env.REACT_APP_GOOGLE_REDIRECT_URI,
            grant_type: "authorization_code",
          }),
        });

        if (!response.ok) {
          throw new Error(`Token exchange failed: ${response.status}`);
        }

        const tokens = await response.json();

        if (!tokens.id_token) {
          throw new Error("No ID token received");
        }

        onSuccess({
          credential: tokens.id_token,
          tokenResponse: tokens,
        });
      } catch (error) {
        console.error("Token exchange error:", error);
        toast.error("Failed to authenticate with Google");
        onError();
      }
    },
    onError: (error) => {
      console.error("Google login error:", error);
      toast.error("Google login failed. Please try again.");
      onError();
    },
    flow: "auth-code",
    scope: "openid email profile",
    redirect_uri: window.location.origin,
  });

  return (
    <a
      onClick={() => !isLoading && login()}
      className={`social-login-btn ${isLoading ? "loading" : ""}`}
      disabled={isLoading}
    >
      <img src={googleImg} alt="Google" className="social-icon" />

      {isLoading ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          Processing...
        </>
      ) : (
        "Continue with Google"
      )}
    </a>
  );
};

const SocialLogin = () => {
  const navigate = useNavigate();
  const { handleSocialLogin, handleFacebookLogin, handleMicrosoftLogin } =
    useSocialLogin();
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [googleMounted, setGoogleMounted] = useState(false);
  const isSocialLoading = useSelector((state) => state.auth.isSocialLogin);
  const activeSocialProvider = useSelector(
    (state) => state.auth.socialLoginProvider,
  );

  useEffect(() => {
    setGoogleMounted(true);
    return () => setGoogleMounted(false);
  }, []);

  const isLoading = (provider) =>
    loadingProvider === provider ||
    (isSocialLoading && activeSocialProvider === provider);

  const handleGoogleSuccess = async (response) => {
    setLoadingProvider("google");

    try {
      if (!response?.credential) {
        throw new Error("No credential received from Google");
      }

      const socialResponse = await handleSocialLogin(
        "google",
        response.credential,
      );

      if (socialResponse?.success) {
        if (socialResponse?.user?.native_language) {
          applyTranslationDynamically(socialResponse.user.native_language);
        }
        toast.success("Login successful");
        navigate("/dashboard");
      } else {
        toast.error(socialResponse?.message || "Google login failed");
      }
    } catch (error) {
      console.error("Google login processing error:", error);
      toast.error(error.message || "An error occurred during Google login");
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleGoogleError = () => {
    console.error("Google login failed");
    setLoadingProvider(null);
  };

  const handleMicrosoftLoginClick = async () => {
    setLoadingProvider("microsoft");
    try {
      const accessToken = await handleMicrosoftLogin();
      const response = await handleSocialLogin("microsoft", accessToken);

      if (response.success) {
        if (response?.user?.native_language) {
          applyTranslationDynamically(response.user.native_language);
        }
        toast.success("Login successful");
        navigate("/dashboard");
      } else {
        toast.error(response.message || "Microsoft login failed");
      }
    } catch (error) {
      console.error("Microsoft login error:", error);
      toast.error(error.message || "An error occurred during Microsoft login");
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleFacebookLoginClick = async () => {
    setLoadingProvider("facebook");
    try {
      const accessToken = await handleFacebookLogin();
      const response = await handleSocialLogin("facebook", accessToken);

      if (response.success) {
        if (response?.user?.native_language) {
          applyTranslationDynamically(response.user.native_language);
        }
        toast.success("Login successful");
        navigate("/dashboard");
      } else {
        toast.error(response.message || "Facebook login failed");
      }
    } catch (error) {
      console.error("Facebook login error:", error);
      toast.error(error.message || "An error occurred during Facebook login");
      setLoadingProvider(null);
    } finally {
      setLoadingProvider(null);
    }
  };

  return (
    <div className="devices_list">
      <ul>
        {ENABLE_GOOGLE && (
          <li>
            {googleMounted && (
              <GoogleOAuthProvider
                clientId={
                  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
                  "981597789953-ca9e564a84gt29ctskupheqfocpeg2qp.apps.googleusercontent.com"
                }
              >
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  isLoading={isLoading("google")}
                />
              </GoogleOAuthProvider>
            )}
          </li>
        )}
        {ENABLE_MICROSOFT && (
          <li>
            <a
              onClick={handleMicrosoftLoginClick}
              className={`social-login-btn ${isLoading("microsoft") ? "loading" : ""}`}
              disabled={isLoading("microsoft")}
            >
              <img src={microsoftImg} alt="Microsoft" className="social-icon" />
              {isLoading("microsoft") ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : (
                "Continue with Microsoft"
              )}
            </a>
          </li>
        )}
        {ENABLE_FACEBOOK && (
          <li>
            <a
              onClick={handleFacebookLoginClick}
              className={`social-login-btn ${isLoading("facebook") ? "loading" : ""}`}
              disabled={isLoading("facebook")}
            >
              <img src={facebookImg} alt="Facebook" className="social-icon" />
              {isLoading("facebook") ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : (
                "Continue with Facebook"
              )}
            </a>
          </li>
        )}
      </ul>
    </div>
  );
};

export default SocialLogin;
